/*
 * Copyright (c) 2023 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import { useEffect, useRef, useState } from "react";
import { AtomicBlockUtils, EditorState, Modifier, convertToRaw, ContentState, convertFromHTML } from 'draft-js';
import { Editor } from "react-draft-wysiwyg";
import { Button, Input, Label, PopoverBody } from "reactstrap";
import { TagPopover } from "../pages/marketing/create/steps/MessageEditorStep";
import DismissablePopover from "./DismissablePopover";
import draftToHtml from 'draftjs-to-html';
import debug from "../util/constants/Debug";
import htmlToDraft from "html-to-draftjs";
import useUpdateState from "../util/useUpdateState";

// Custom IMAGE entity transform for draftToHtml.
//
// - `fitScreen` flag: emits an image that scales to its container
//   (max-width/max-height: 100%, display: block).
// - Alignment: rdw stores alignment as "left" | "right" | "none" (the "C"
//   button writes "none", as does a freshly inserted image). Left/right
//   are wrapped in a `<div style="text-align:X;">` so the saved HTML
//   renders with the correct alignment. "none" is emitted unwrapped to
//   match rdw's native default and to avoid introducing an extra paragraph
//   block during HTML -> editor state round-trips.
// - `data-fit-screen` attribute is used as a round-trip marker so the flag
//   survives HTML -> editor state conversion (see `editorChunkRenderer`).
export const editorEntityTransform = (entity) => {
  if (entity.type !== "IMAGE") return undefined;
  const { src, height, width, alt, alignment, fitScreen } = entity.data;
  const altAttr = alt ? ` alt="${alt}"` : "";
  const fitAttr = fitScreen ? ` data-fit-screen="true"` : "";

  const imgStyle = fitScreen
    ? "max-width: 100%; max-height: 100vh; width: min-content; height: auto; object-fit: contain; display: block; flex: 0 1 auto; min-width: 0;"
    : `height:${height};width:${width};`;
  const imgTag = `<img src="${src}"${altAttr}${fitAttr} style="${imgStyle}"/>`;

  if (alignment === "left" || alignment === "right") {
    return `<div style="text-align:${alignment};">${imgTag}</div>`;
  }
  return imgTag;
};

// Convenience wrapper so all call sites serialize the editor state the same way.
export const editorStateToHtml = (editorState) =>
  draftToHtml(convertToRaw(editorState.getCurrentContent()), undefined, false, editorEntityTransform);

// Custom chunk renderer for html-to-draftjs. Preserves the `fitScreen` flag on
// <img> elements carrying the `data-fit-screen="true"` marker, and recovers
// image alignment from the wrapping element's `text-align` (our exported
// format for left/right) or from legacy `float` styles.
const editorChunkRenderer = (nodeName, node) => {
  if (nodeName !== "img") return undefined;
  const fitScreen = node.getAttribute?.("data-fit-screen") === "true";

  const rawAlign = node.parentElement?.style?.textAlign
                || node.parentElement?.style?.float
                || node.style?.float
                || "";
  // rdw stores alignment as "left" | "right" | "none". Anything else
  // (empty, "center", "inherit", etc.) falls back to "none".
  const alignment = (rawAlign === "left" || rawAlign === "right") ? rawAlign : "none";

  return {
    type: "IMAGE",
    mutability: "MUTABLE",
    data: {
      src: node.getAttribute?.("src") ?? "",
      alt: node.getAttribute?.("alt") ?? "",
      height: fitScreen ? "auto" : (node.style?.height || "auto"),
      width: fitScreen ? "auto" : (node.style?.width || "auto"),
      alignment,
      fitScreen
    }
  };
};

// Convenience wrapper so all call sites deserialize HTML the same way.
// If html-to-draftjs produces an atomic block without a backing entity key
// (can happen when the HTML is malformed or hand-edited into an invalid
// state), draft-js throws "Unknown DraftEntity key: null." as soon as the
// editor tries to render. Fall back to an empty editor state in that case
// instead of crashing.
export const htmlToEditorState = (html, showErrorAlert=true) => {
  const safeBuild = (input) => {
    const blocksFromHTML = htmlToDraft(input || "", editorChunkRenderer);
    const blocks = blocksFromHTML.contentBlocks || [];
    const hasBrokenAtomic = blocks.some(
      (block) => block.getType() === "atomic" && !block.getEntityAt(0)
    );
    if (hasBrokenAtomic) return null;

    // Our exporter wraps left/right-aligned images in
    // `<div style="text-align:X"><img/></div>`. html-to-draftjs turns the
    // wrapping <div> into its own empty unstyled block (with block-level
    // text-align data) *in addition* to the atomic block produced for the
    // <img> via editorChunkRenderer. Drop that artefact block so
    // save/reload cycles don't accumulate ghost paragraphs. The block-data
    // text-align check is what distinguishes the artefact from a user's
    // intentional empty <p></p> (which has no block data).
    const cleanedBlocks = blocks.filter((block, idx) => {
      if (block.getType() !== "unstyled") return true;
      if (block.getText().length > 0) return true;
      const blockData = block.getData();
      const hasTextAlign = blockData && typeof blockData.get === "function"
        && blockData.get("text-align") != null;
      if (!hasTextAlign) return true;
      return blocks[idx + 1]?.getType() !== "atomic";
    });

    return ContentState.createFromBlockArray(cleanedBlocks, blocksFromHTML.entityMap);
  };

  const contentState = safeBuild(html);
  if (contentState) return EditorState.createWithContent(contentState);

  if (showErrorAlert) {
    alert("The HTML content is invalid and could not be loaded into the editor. The editor has been reset to empty.");
  }
  return EditorState.createWithContent(safeBuild(""));
};

const HtmlButton = ({ onClick }) => (
  <div className="rdw-option-wrapper" title="Edit HTML" onClick={onClick}>
    <i className="icon bi bi-code-slash" />
  </div>
);

const TagButton = ({ onTag }) => {
  // tag popover state
  const [ popover, setPopover ] = useState(false);

  // handler
  const handleTag = (tag) => {
    onTag?.(tag);
    setPopover(false);
  };

  return (
    <>
      <div className="rdw-option-wrapper" id="editor-tag" title="Insert Tag" onClick={() => setPopover(true)}>
        <i className="icon bi bi-tag" />
      </div>
      <TagPopover isOpen={popover} toggle={() => setPopover(prev => !prev)} target="editor-tag" onTag={handleTag}/>
    </>
  )
};

const TextButton = ({ onClick }) => (
  <div className="rdw-option-wrapper" title="Edit Text" onClick={onClick}>
    <i className="icon bi bi-fonts" />
  </div>
);

const FileButton = ({ onClick }) => (
  <div className="rdw-option-wrapper" title="Upload File" onClick={onClick}>
    <i className="icon bi bi-file-earmark-arrow-up" />
  </div>
);

// Custom image toolbar button. Mirrors the default react-draft-wysiwyg image
// option (URL / width / height) and adds a "Fit screen" checkbox which forces
// the image to render at auto size with max-width/max-height: 100% and
// display: block in the exported HTML.
const ImageButton = ({ editorState, onChange }) => {
  const initialState = { src: "", width: "auto", height: "auto", alt: "", fitScreen: false };
  const [popover, setPopover] = useState(false);
  const [state,, updateState] = useUpdateState(initialState);

  useEffect(() => {
    if (popover) updateState(initialState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popover]);

  const insertImage = () => {
    if (!state.src) return;
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity("IMAGE", "MUTABLE", {
      src: state.src,
      alt: state.alt,
      width: state.fitScreen ? "auto" : state.width,
      height: state.fitScreen ? "auto" : state.height,
      alignment: "none",
      fitScreen: state.fitScreen
    });
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const newEditorState = EditorState.set(editorState, { currentContent: contentStateWithEntity });
    onChange(AtomicBlockUtils.insertAtomicBlock(newEditorState, entityKey, " "));
    setPopover(false);
  };

  return (
    <>
      <div className="rdw-option-wrapper" id="editor-image" title="Insert Image" onClick={() => setPopover(prev => !prev)}>
        <i className="icon bi bi-image" />
      </div>
      <DismissablePopover
        isOpen={popover}
        toggle={() => setPopover(prev => !prev)}
        target="editor-image"
        placement="bottom"
      >
        <PopoverBody style={{ width: "260px" }}>
          <Label className="fw-bold mb-1">Image URL</Label>
          <Input
            value={state.src}
            placeholder="https://..."
            onChange={(e) => updateState({ src: e.target.value })}
          />
          <div className="d-flex align-items-center mt-2">
            <Input
              id="editor-image-fit"
              type="checkbox"
              checked={state.fitScreen}
              onChange={(e) => updateState({ fitScreen: e.target.checked })}
            />
            <Label for="editor-image-fit" className="ms-2 mb-0">Fit screen</Label>
          </div>
          <Label className="fw-bold mt-2 mb-1">Width</Label>
          <Input
            value={state.fitScreen ? "auto" : state.width}
            disabled={state.fitScreen}
            onChange={(e) => updateState({ width: e.target.value })}
          />
          <Label className="fw-bold mt-2 mb-1">Height</Label>
          <Input
            value={state.fitScreen ? "auto" : state.height}
            disabled={state.fitScreen}
            onChange={(e) => updateState({ height: e.target.value })}
          />
          <Button className="mt-3 w-100" color="primary" onClick={insertImage} disabled={!state.src}>
            Insert
          </Button>
        </PopoverBody>
      </DismissablePopover>
    </>
  );
};

const TextEditor = ({
  state,
  updateState,
  showTag = false
}) => {
  const htmlRef = useRef(null);

  // styles
  const editorBorderStyle = {
    border: "1px solid #F1F1F1",
    borderRadius: "2px",
  };

  // html file upload
  const fileInputRef = useRef(null);

  const handleFileSelected = async (event) => {
    // validate 
    const selectedFile = event.target.files[0];
    if (!selectedFile) {
      debug.warn("Invalid file");
      return;
    }

    // read file in
    try {
      const fileReader = new FileReader();
      fileReader.onload = (e) => {
        const content = e.target.result;
        htmlRef.current.value = content;
        updateState({ htmlContent: content });
      }
      fileReader.readAsText(selectedFile);
    } catch (error) {
      debug.error("Error reading file: ", error);
      alert("Error reading file");
    }
  };

  // text/html mode toggle
  const toggleMode = () => {
    if (state.textMode) {
      // set to HTML edit mode
      const html = editorStateToHtml(state.editorState);
      updateState({ textMode: false, htmlContent: html });
    } else {
      // set to rich text mode
      updateState({ textMode: true, editorState: htmlToEditorState(state.htmlContent) });
    }
  };

  // handlers
  const handleKeyPress = (event) => {
    switch (event.key) {
      // add 2 spaces for tab
      case "Tab": {
        event.preventDefault();
        const start = htmlRef.current.selectionStart;
        const end = htmlRef.current.selectionEnd;
        const value = htmlRef.current.value;
        htmlRef.current.value = value.substring(0, start) + "  " + value.substring(end);
        htmlRef.current.selectionStart = htmlRef.current.selectionEnd = start + 2;
        break;
      }
      default: break;
    }
  };

  const handleInsertTag = (tag) => {
    const { editorState } = state;
    const contentState = Modifier.replaceText(
      editorState.getCurrentContent(),
      editorState.getSelection(),
      tag,
      editorState.getCurrentInlineStyle(),
    );
    updateState({ editorState: EditorState.push(editorState, contentState, 'insert-characters') });
  };

  // editor buttons
  const editorButtons = [
    <ImageButton editorState={state.editorState} onChange={(editorState) => updateState({ editorState })} />,
    <HtmlButton onClick={toggleMode} />
  ];
  if (showTag) {
    editorButtons.push(<TagButton onTag={handleInsertTag} />);
  }

  // Omit the built-in `image` option; we use a custom ImageButton (above) that
  // supports the additional "Fit screen" mode.
  const toolbarOptions = {
    options: [
      'inline', 'blockType', 'fontSize', 'fontFamily', 'list', 'textAlign',
      'colorPicker', 'link', 'embedded', 'emoji', 'remove', 'history'
    ]
  };

  return (
    <div className="w-100" style={{ height: "500px" }}>
      <div className={`fill-container ${state.textMode ? "d-block" : "d-none"}`}>
        <Editor
          editorState={state.editorState}
          editorStyle={{ ...editorBorderStyle, height: "425px" }}
          onEditorStateChange={(editorState) => updateState({ editorState })}
          toolbarCustomButtons={editorButtons}
          toolbar={toolbarOptions}
        />
      </div>
      <div className={`d-flex flex-column fill-container ${state.textMode ? "d-none" : "d-block"}`}>
        <div className="d-flex p-2 mb-2" style={editorBorderStyle}>
          <TextButton onClick={toggleMode} />
          <FileButton onClick={() => fileInputRef.current.click()} />
        </div>
        <textarea
          className="w-100 flex-dynamic"
          value={state.htmlContent}
          onKeyDown={handleKeyPress}
          onChange={(e) => updateState({ htmlContent: e.target.value })}
          style={{
            ...editorBorderStyle,
            fontFamily: "monospace",
            tabSize: 2,
            display: "block",
            width: "100%",
            lineHeight: "1.4",
            padding: "10px",
            resize: "none",
            overflowX: "auto",
            overflowY: "auto",
          }}
        />
        <input ref={fileInputRef} type="file" accept=".txt,.html" style={{ display: 'none' }} onChange={handleFileSelected} />
      </div>
    </div>
  );
};

export default TextEditor;
